/* ============================================
   PaginatedSection — Auto-paginated A4 section
   Measures rendered HTML content and splits it
   across multiple A4 pages when it overflows.
   Tables are split row-by-row when they exceed
   the available space.
   ============================================ */
import { useState, useEffect, useRef, useCallback } from 'react'
import { sanitizeHtml, processContent } from '@/lib/utils'
import { expandirBloques, type DatosBloques } from '@/lib/cotizacion-bloques'

// A4 at 96dpi: 794×1123px
// Page structure: header(~16) + ruc(~25) + bodyPadding(24+60) + footer(48 abs)
// Usable content area: 1123 - 16 - 25 - 24 - 60 ≈ 998px
const FIRST_PAGE_BODY = 955  // with section title (~40px)
const CONT_PAGE_BODY = 985   // continuation pages (small label ~12px)

interface PaginatedSectionProps {
  titulo: string
  contenido: string | string[]
  pc: string
  empresaNombre: string
  empresaWebsite: string
  clienteRuc: string
  startPage: number
  sectionId?: string
  onPageCount?: (count: number) => void
  /**
   * Datos de la cotización para los bloques generados ({{tabla_inversion}},
   * {{cronograma_pagos}}). Sin esto el marcador se vería crudo en la vista web
   * aunque el PDF lo resuelva — las dos superficies renderizan por su cuenta.
   */
  bloques?: DatosBloques | null
}

export default function PaginatedSection({
  titulo,
  contenido,
  pc,
  empresaNombre,
  empresaWebsite,
  clienteRuc,
  startPage,
  sectionId,
  onPageCount,
  bloques,
}: PaginatedSectionProps) {
  const measureRef = useRef<HTMLDivElement>(null)
  const [pages, setPages] = useState<string[]>([])

  const rawContent = Array.isArray(contenido) ? contenido.join('') : contenido
  // Los bloques se expanden DESPUÉS de sanitizar el contenido del autor: lo que
  // inyectamos lo armamos acá y no necesita pasar por el saneador.
  const htmlContent = expandirBloques(sanitizeHtml(processContent(rawContent)), bloques)

  const paginate = useCallback(() => {
    if (!measureRef.current) return

    const container = measureRef.current
    container.innerHTML = htmlContent

    const children = Array.from(container.children) as HTMLElement[]
    if (children.length === 0) {
      setPages([htmlContent])
      onPageCount?.(1)
      return
    }

    const result: string[] = []
    let currentPageHtml = ''
    let currentHeight = 0
    let maxHeight = FIRST_PAGE_BODY

    const getElHeight = (el: HTMLElement) => {
      const style = getComputedStyle(el)
      return el.offsetHeight +
        parseInt(style.marginTop || '0') +
        parseInt(style.marginBottom || '0')
    }

    const startNewPage = () => {
      if (currentPageHtml) result.push(currentPageHtml)
      currentPageHtml = ''
      currentHeight = 0
      maxHeight = CONT_PAGE_BODY
    }

    /** Split a <table> element row-by-row across pages */
    const splitTable = (table: HTMLElement) => {
      const thead = table.querySelector('thead')
      const theadHtml = thead ? thead.outerHTML : ''
      const theadHeight = thead ? thead.offsetHeight : 0
      // El <colgroup> lleva los anchos de columna y el <tfoot> la fila de total. Al
      // rearmar la tabla hay que reponerlos: sin el colgroup las columnas se reparten
      // solas (y la primera, con `white-space: nowrap`, ahoga a las demás), y sin el
      // tfoot el total simplemente desaparece de la vista pública aunque esté en el PDF.
      const colgroup = table.querySelector('colgroup')
      const colgroupHtml = colgroup ? colgroup.outerHTML : ''
      const tfoot = table.querySelector('tfoot')
      const tfootHtml = tfoot ? tfoot.outerHTML : ''
      const tfootHeight = tfoot ? tfoot.offsetHeight : 0
      const rows = Array.from(table.querySelectorAll('tbody tr')) as HTMLElement[]

      if (rows.length === 0) {
        // No tbody rows — treat as atomic
        currentPageHtml += table.outerHTML
        currentHeight += getElHeight(table)
        return
      }

      // Get table wrapper attributes (class, style, etc.)
      const tableOpen = table.outerHTML.match(/^<table[^>]*>/)?.[0] || '<table>'

      let rowsForCurrentPage: string[] = []
      let accHeight = theadHeight // Start with thead height
      const tableMargin = 20 // margin-bottom of cot-table

      // Check if we can fit at least the thead + 1 row on the current page
      const firstRowHeight = rows[0].offsetHeight
      if (currentHeight + accHeight + firstRowHeight + tableMargin > maxHeight && currentPageHtml) {
        // Can't fit even 1 row — start new page first
        startNewPage()
        accHeight = theadHeight
      }

      for (const row of rows) {
        const rowHeight = row.offsetHeight

        if (currentHeight + accHeight + rowHeight + tableMargin + tfootHeight > maxHeight) {
          // Flush current rows as a partial table (el tfoot va solo en el último tramo)
          if (rowsForCurrentPage.length > 0) {
            currentPageHtml += `${tableOpen}${colgroupHtml}${theadHtml}<tbody>${rowsForCurrentPage.join('')}</tbody></table>`
            startNewPage()
            rowsForCurrentPage = []
            accHeight = theadHeight
          }
        }

        rowsForCurrentPage.push(row.outerHTML)
        accHeight += rowHeight
      }

      // Flush remaining rows — este es el tramo que cierra la tabla, así que lleva el tfoot
      if (rowsForCurrentPage.length > 0) {
        currentPageHtml += `${tableOpen}${colgroupHtml}${theadHtml}<tbody>${rowsForCurrentPage.join('')}</tbody>${tfootHtml}</table>`
        currentHeight += accHeight + tfootHeight + tableMargin
      }
    }

    for (const child of children) {
      const childHeight = getElHeight(child)
      const isTable = child.tagName === 'TABLE'

      if (isTable) {
        // Tables get special row-by-row splitting
        splitTable(child)
      } else if (currentHeight + childHeight > maxHeight && currentPageHtml) {
        // Non-table element overflows — start new page
        startNewPage()
        currentPageHtml += child.outerHTML
        currentHeight += childHeight
      } else {
        currentPageHtml += child.outerHTML
        currentHeight += childHeight
      }
    }

    // Add remaining content
    if (currentPageHtml) result.push(currentPageHtml)

    setPages(result.length > 0 ? result : [htmlContent])
    onPageCount?.(result.length || 1)
  }, [htmlContent, onPageCount])

  useEffect(() => {
    const timer = setTimeout(paginate, 50)
    return () => clearTimeout(timer)
  }, [paginate])

  return (
    <>
      {/* Hidden measurer — offscreen, same width as A4 body */}
      <div
        ref={measureRef}
        className="cot-section-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '714px', // 794 - 40*2 padding
          visibility: 'hidden',
          fontSize: '10pt',
          fontFamily: "'Inter', sans-serif",
          lineHeight: 1.7,
        }}
      />

      {/* Render pages */}
      {pages.map((pageHtml, pageIdx) => (
        <div
          key={`${titulo}-p${pageIdx}`}
          id={pageIdx === 0 ? sectionId : undefined}
          className="cotizacion-preview cot-page"
          style={{ marginTop: 24 }}
        >
          <div className="cot-page-header" style={{ background: pc }} />
          <div className="cot-page-ruc">{clienteRuc}</div>
          <div className="cot-body">
            {pageIdx === 0 && (
              <h3 className="cot-section-title" style={{ borderColor: pc }}>
                {titulo}
              </h3>
            )}
            {pageIdx > 0 && (
              <div style={{ fontSize: '8pt', color: 'var(--text-muted)', marginBottom: 12, fontStyle: 'italic' }}>
                {titulo} (continuación)
              </div>
            )}
            <div
              className="cot-section-content"
              dangerouslySetInnerHTML={{ __html: pageHtml }}
            />
          </div>
          <div className="cot-footer" style={{ background: pc }}>
            <span>{empresaNombre}</span>
            <span>Pág. {startPage + pageIdx}</span>
            <span>{empresaWebsite}</span>
          </div>
        </div>
      ))}
    </>
  )
}
